// src/App.jsx
//
// ROUTING-PHASE-1: App entry point.
// Wraps the application in BrowserRouter and renders AppShell.
// All state management and routing logic lives in AppShell.jsx.
//
// Pure navigation utility functions (applyViewGuard, derivePageLabel,
// deriveNavLocations, deriveNavLeases) are re-exported from App.jsx
// for backward compatibility with tests that import them from here.
// Their implementations have moved to src/app/navUtils.js to avoid
// a circular import between App ↔ AppShell.

import React from "react";
import { BrowserRouter } from "react-router-dom";
import { AppShell } from "./AppShell.jsx";
import { GanttListTestFixture } from "./features/leases/pages/components/GanttListTestFixture.jsx";
import { HeadingTooltipTestFixture } from "./components/HeadingTooltipTestFixture.jsx";
import { DataHealthView } from "./features/data-health/DataHealthView.jsx";
import { ToastHost } from "./components/toastService.jsx";
import { DevActAsFeedback } from "./components/DevActAsFeedback.jsx";

// Re-export pure functions so existing test imports (e.g.
// `import { derivePageLabel } from "./App.jsx"`) keep working without change.
export {
  applyViewGuard,
  derivePageLabel,
  deriveNavLocations,
  deriveNavLeases,
  resolveNavNeighbors,
  PAGE_LABELS,
  REPORT_VIEWS,
} from "./app/navUtils.js";

// Keep the legacy constants available so any consumer that imports them
// from App.jsx via a named import doesn't break.
export { PAGE_LABELS as PAGE_LABELS_MAP } from "./app/navUtils.js";

export default function App() {
  // DEV-ONLY: render minimal test fixtures for Playwright e2e tests that
  // need direct access to components not reachable via normal navigation.
  // Vite strips this branch in production builds (import.meta.env.DEV === false).
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("__e2e") === "gantt-list") {
    return <GanttListTestFixture />;
  }
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("__e2e") === "tooltip-flip") {
    return <HeadingTooltipTestFixture />;
  }
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("__e2e") === "data-health") {
    return <DataHealthView />;
  }

  return (
    <BrowserRouter>
      <DevActAsFeedback />
      <AppShell />
      <ToastHost />
    </BrowserRouter>
  );
}
